import { useEffect, useRef } from "react";

/**
 * Reveal-on-scroll hook.
 * Uses IntersectionObserver + MutationObserver so that elements added
 * AFTER initial render (e.g. via async fetch) are still observed.
 * Includes a safety net: after 1.2s, force-reveal anything still hidden
 * (guards against edge cases where IO never fires, e.g. some mobile browsers
 * or when the page is loaded already scrolled).
 */
export default function useReveal() {
    const ref = useRef(null);

    useEffect(() => {
        if (!ref.current) return;
        const root = ref.current;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("is-visible");
                        io.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12 }
        );

        const observeAll = () => {
            root.querySelectorAll(".reveal:not(.is-visible)").forEach((n) => io.observe(n));
        };

        observeAll();

        const mo = new MutationObserver(() => observeAll());
        mo.observe(root, { childList: true, subtree: true });

        // Safety net — guarantee content is visible even if IO never fires
        const safetyTimer = setTimeout(() => {
            root.querySelectorAll(".reveal:not(.is-visible)").forEach((n) => {
                n.classList.add("is-visible");
            });
        }, 1200);

        return () => {
            io.disconnect();
            mo.disconnect();
            clearTimeout(safetyTimer);
        };
    }, []);

    return ref;
}
